import { motion } from "framer-motion";
import { Shield } from "lucide-react";
import { useCms } from "@/hooks/useCms";
import { SeoHead } from "@/components/SeoHead";

type LegalSection = { title: string; body: string };

function renderBody(body: string) {
  const paragraphs = body.split("\n\n");
  return paragraphs.map((para, i) => {
    const lines = para.split("\n");
    const bullets = lines.filter(l => l.startsWith("•") || l.startsWith("-")).map(l => l.replace(/^[•-]\s*/, ""));
    const texts = lines.filter(l => !l.startsWith("•") && !l.startsWith("-"));
    return (
      <div key={i} className="space-y-2">
        {texts.map((t, j) => t.trim() && <p key={j}>{t}</p>)}
        {bullets.length > 0 && (
          <ul className="list-disc pl-6 space-y-1.5">
            {bullets.map((b, j) => <li key={j}>{b}</li>)}
          </ul>
        )}
      </div>
    );
  });
}

export default function PrivacyPolicy() {
  const { getMeta, getText } = useCms();
  const lastUpdated = getText("privacy_policy", "Last updated: March 2025");
  const { sections = [] } = getMeta<{ sections?: LegalSection[] }>("privacy_policy", {});

  return (
    <>
      <SeoHead
        title="Privacy Policy"
        description="Eliada Sports Agency's Privacy Policy — how we collect, use, and protect your personal information."
        path="/privacy-policy"
      />
      <div className="min-h-screen bg-background">
        <div className="relative py-28 bg-gradient-to-b from-[#060b14] to-background overflow-hidden">
          <div className="absolute inset-0 opacity-5" style={{ backgroundImage: "radial-gradient(circle at 50% 50%, hsl(45 100% 50%), transparent 60%)" }} />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/20 mb-6">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-display text-5xl font-bold text-white tracking-tight mb-4">Privacy Policy</h1>
              <p className="text-muted-foreground text-lg">{lastUpdated}</p>
            </motion.div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert prose-lg max-w-none space-y-12"
          >
            {sections.length > 0 ? (
              sections.map((sec, i) => (
                <div key={i} className="space-y-4">
                  <h2 className="font-display text-2xl font-bold text-white border-b border-white/10 pb-3">{sec.title}</h2>
                  <div className="text-muted-foreground leading-relaxed space-y-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_strong]:text-white [&_a]:text-primary [&_a:hover]:underline">
                    {renderBody(sec.body)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-muted-foreground">Privacy policy content is being updated. Please check back soon.</div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
